import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../auth'

/**
 * Generic once-per-account UI bits, backed by the `user_flags` table
 * (one row per (profile, flag), RLS-scoped to the current user).
 *
 * Current flag families:
 *   tip:{game}_weekly          — weekly explainer overlay was seen
 *   wkres:{game}:{event_id}    — a settled weekly result was acknowledged
 */

// `user_flags` isn't in the generated DB types, so use an untyped
// client (same pattern as user_game_notes and other post-generation
// tables).
const db = supabase as any // eslint-disable-line @typescript-eslint/no-explicit-any

/** Weekly game ids that carry an explainer overlay (TIP_FLAG below). */
export type WeeklyGameId = 'ideatthat' | 'colorful' | 'three_step'

/**
 * Every game that can settle a weekly result, matching `list_my_trophies`
 * and `list_my_unseen_weekly_results_v2`. Wider than WeeklyGameId because
 * the two word games have no explainer overlay: their first-time tip is
 * the daily's own onboarding, so they must not widen TIP_FLAG's keyset.
 */
export type WeeklyResultGame = WeeklyGameId | 'fake_out' | 'close_enough'

export const TIP_FLAG: Record<WeeklyGameId, string> = {
  ideatthat: 'tip:ideatthat_weekly',
  colorful: 'tip:colorful_weekly',
  three_step: 'tip:three_step_weekly',
}

export async function hasUserFlag(flag: string): Promise<boolean> {
  const { data, error } = await db
    .from('user_flags')
    .select('flag')
    .eq('flag', flag)
    .maybeSingle()
  if (error) throw error
  return Boolean(data)
}

export async function setUserFlag(flag: string): Promise<void> {
  const { data: userData } = await supabase.auth.getUser()
  const uid = userData.user?.id
  if (!uid) return
  const { error } = await db
    .from('user_flags')
    .upsert({ profile_id: uid, flag }, { onConflict: 'profile_id,flag', ignoreDuplicates: true })
  if (error) throw error
}

/**
 * Drives a weekly explainer overlay: shows it only after confirming
 * the account has NOT seen it (no flash for returning players), and
 * records the flag on dismiss. Guests get it too — their anonymous
 * profile can hold flags, and the explainer is most useful on a
 * first look.
 */
export function useWeeklyTip(game: WeeklyGameId) {
  const { session, profile, configured } = useAuth()
  const ready = Boolean(configured && session && profile)
  const [showTip, setShowTip] = useState(false)

  useEffect(() => {
    if (!ready) return
    let cancelled = false
    hasUserFlag(TIP_FLAG[game])
      .then(seen => { if (!cancelled && !seen) setShowTip(true) })
      .catch(() => { /* offline / RLS hiccup — just skip the tip */ })
    return () => { cancelled = true }
  }, [ready, game])

  const dismissTip = useCallback(() => {
    setShowTip(false)
    setUserFlag(TIP_FLAG[game]).catch(() => { /* worst case: shows once more */ })
  }, [game])

  return { showTip, dismissTip }
}

/** Make every explainer overlay show once more (Profile → reset tips). */
export async function clearTipFlags(): Promise<void> {
  const { error } = await db
    .from('user_flags')
    .delete()
    .like('flag', 'tip:%')
  if (error) throw error
}

// ── Settled weekly results ───────────────────────────────────────────

export interface UnseenWeeklyResult {
  game: WeeklyResultGame
  event_id: string
  week_start: string          // ISO Monday
  label: string | null        // three_step: the prompt; ideatthat: my dish title
  my_score: number            // colorful: crowd matches 0–6; word games: week's points; others: votes
  my_rank: number             // dense rank — ties share a placement
  participant_count: number
  is_winner: boolean          // rank 1 with 2+ human entrants, the same rule as the trophy shelf
}

/**
 * Recently-settled weeks the user participated in and hasn't been
 * shown yet. Empty for guests (anonymous accounts can't submit).
 *
 * v2 adds FAKE OUT and CLOSE ENOUGH. The v1 RPC stays in place for the
 * clients already shipped: their popup indexes a three-key art map with
 * the game id, and an unknown id would throw at boot.
 */
export async function fetchUnseenWeeklyResults(): Promise<UnseenWeeklyResult[]> {
  const { data, error } = await db.rpc('list_my_unseen_weekly_results_v2')
  if (error) throw error
  return Array.isArray(data) ? (data as UnseenWeeklyResult[]) : []
}

/** Mark results as seen so the popup never repeats them. */
export async function ackWeeklyResults(results: UnseenWeeklyResult[]): Promise<void> {
  if (results.length === 0) return
  const { data: userData } = await supabase.auth.getUser()
  const uid = userData.user?.id
  if (!uid) return
  const rows = results.map(r => ({
    profile_id: uid,
    flag: `wkres:${r.game}:${r.event_id}`,
  }))
  const { error } = await db
    .from('user_flags')
    .upsert(rows, { onConflict: 'profile_id,flag', ignoreDuplicates: true })
  if (error) throw error
}
